/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomLegalRequirements.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CustomSelectV2 from "../common/select/CustomSelectV2";
import CustomDataGridV2 from "../common/datagrid/CustomDataGridV2";
import { useDispatch } from "react-redux";
import { HandleApiActions, legalRequirements } from "../../../redux/actions";
import dayjs from "dayjs";
import AddButtonV2 from "../common/button/AddButtonV2";
import AddDialog from "../projectConfig/AddDialog";
import CustomFormTextField from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import ChipsTextField from "../../text-fields/ChipsTextField";
import LegalCustomDatePicker from "../common/datepicker/LegalCustomDatePicker";
import EnterSelectTextField from "../../text-fields/EnterSelectTextField";

import CustomStatusOverrideSelect from "./CustomStatusOverrideSelect";
import EditIcon from "@mui/icons-material/Edit";
import CustomButtonV2 from "../common/button/CustomButtonV2";
import SyncIcon from "@mui/icons-material/Sync";
import SyncLegalRequirementsDialog from "./SyncLegalRequirementsDialog";

function CustomLegalRequirements({
  legalStandardsData,
  handleGetLegalStandardList,
  refreshLegalStandards,
}) {
  const dispatch = useDispatch();

  const [selectedChapter, setSelectedChapter] = useState("");
  const [tableRowData, setTableRowData] = useState([]);
  const [isEditingRowId, setIsEditingRowId] = useState(null);
  const [editRowData, setEditRowData] = useState(null);
  const [addLRDialog, setAddLRDialog] = useState(false);

  const [reqNumber, setReqNumber] = useState("");
  const [reqNumberError, setReqNumberError] = useState("");
  const [chapter, setChapter] = useState([]);
  const [chapterError, setChapterError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(null);
  const [validTill, setValidTill] = useState(null);
  const items = legalStandardsData?.[0]?.items || [];
  const [chaptersOptionsList, setChaptersOptionsList] = useState([]);
  const [openForVersion, setOpenForVersion] = useState(false);
  const [openEditLR, setOpenEditLR] = useState(false);
  const [isOverrideChecked, setIsOverrideChecked] = useState(false);
  const [status, setStatus] = useState("");
  const [initialStandardData, setInitialStandardData] = useState({});
  const [lrCount, setLRCount] = useState(null);
  const [openSyncDialog, setOpenSyncDialog] = useState(false);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedEditLRDetails, setSelectedEditLRDetails] = useState(null)
  // Version dropdown list
  const versionOptions = useMemo(() => {
    return [...items]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // descending
      .map((item) => ({
        label: `${item.version} - ${dayjs(item?.created_at).format(
          "DD MMM YYYY",
        )}`,
        value: item.version,
      }));
  }, [items]);

  const [selectedVersion, setSelectedVersion] = useState(
    items?.[items.length - 1]?.version || "",
  );

  // Current selected standard
  const standard = useMemo(
    () => items.find((i) => i.version === selectedVersion) || {},
    [items, selectedVersion],
  );


  // -------------------------------
  // GET API: Legal Requirement List
  // -------------------------------
  const handleGetLegalRequirementsList = async (
    selectedLSId,
    selectedChapter,
  ) => {
    const resp = await dispatch(
      HandleApiActions({
        ...legalRequirements.get_legal_requirements_main_details,
        params: {
          page_number: page,
          page_size: rowsPerPage,
          ...(selectedChapter && { chapter: selectedChapter }),
          ls_id: selectedLSId,
        },
        show_toast: false,
      }),
    );


    setTableRowData(resp?.data || []);
    setLRCount(resp?.total_count || null);
  };

  useEffect(() => {
    if (!items?.length) return;

    const firstChapter = items[0]?.chapters?.[0] || null;
    setSelectedChapter(firstChapter);
  }, [selectedVersion, items]);
  // -------------------------------
  // ASYNC USEEFFECT
  // -------------------------------
  useEffect(() => {
    if (!standard?.ls_id) return;

    if (
      standard?.ls_id &&
      selectedChapter !== null &&
      selectedChapter !== "" &&
      selectedVersion !== ""
    ) {
      handleGetLegalRequirementsList(standard.ls_id, selectedChapter);
    }
  }, [standard?.ls_id, selectedChapter, selectedVersion, page, rowsPerPage]);

  useEffect(() => {
    if (standard) {
      //  Chapters options list for select option legal standard
      let filteredChaptersOptionsListS = standard?.chapters?.map((el) => {
        return {
          value: el,
          label: el,
        };
      });

      setChaptersOptionsList(filteredChaptersOptionsListS || []);
    }
  }, [standard, addLRDialog]);

  // -------------------------------
  // ADD NEW ROW (UI button)
  // -------------------------------
  const handleAddRow = () => {
    const newRow = {
      id: Date.now(),
      isNew: true,
      req_number: "",
      type_of_changes: "NEW",
      description: "",
      effective_from: "",
      valid_till: "",
    };

    setTableRowData((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return [...safePrev, newRow];
    });

    setIsEditingRowId(newRow.id);
    setEditRowData(newRow);
  };
  const handleEditChange = (field, value) => {
    setEditRowData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleEditRow = (row) => {
    setSelectedEditLRDetails(row)
    setReqNumber(row?.req_number);

    setChapter(row?.originalRow?.chapter);
    setIsOverrideChecked(true);
    setStatus(row?.originalRow?.type_of_changes);
    setDescription(row?.description || "");
    setEffectiveFrom(
      dayjs(row.effective_from, "MM-YYYY").startOf("month") || null,
    );
    setValidTill(dayjs(row.valid_till, "MM-YYYY").startOf("month") || null);
    setOpenEditLR(true);
    setIsEditingRowId(row?.originalRow?.lr_id);
    setInitialStandardData({
      name: standard?.name || "",
      standard_number:
        standard?.standard_number || standard?.standardNumber || "",
      revision: standard?.revision || "",
      description: standard?.description || "",
      summary: standard?.summary || "",
      chapters: standard?.chapters || [],
      module_type: standard?.module_type || standard?.module_type || 1,
      markets: standard?.markets || [],
      effective_from: standard?.effective_from
        ? dayjs(standard.effective_from, "MM-YYYY").startOf("month")
        : null,
      valid_till: standard?.valid_till
        ? dayjs(standard.valid_till, "MM-YYYY").startOf("month")
        : null,
      legal_req: standard?.legal_req || [],
    });
    setEditRowData(row);
  };
  const handleCancelEdit = (id) => {
    setTableRowData((prev) => prev.filter((row) => row.id !== id));
    setIsEditingRowId(null);
    setEditRowData({});
  };
  const handleSaveRow = (id) => {
    setTableRowData((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, ...editRowData, isNew: false } : item,
      ),
    );

    setIsEditingRowId(null);
    setEditRowData({});
  };
  const handleDeleteRow = (id) => {
    setTableRowData((prev) => prev.filter((item) => item.id !== id));
  };


  // -------------------------------
  // COLUMNS
  // -------------------------------
  const isEditDisabledLR = () => {
    if (!selectedEditLRDetails) return true;

    // description validation
    if (!description?.trim() || description.trim().length < 3) return true;

    const original = selectedEditLRDetails?.originalRow || {};

    const isChanged =
      description?.trim() !== (original?.description || "") ||
      status !== original?.type_of_changes ||
      chapter?.trim() !== (original?.chapter || "") ||
      dayjs(effectiveFrom).format("MM-YYYY") !== original?.effective_from ||
      dayjs(validTill).format("MM-YYYY") !== original?.valid_till;

    return !isChanged;
  };
  const COLUMNS = [
    {
      field: "s_no",
      headerName: "S. No",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const sourceValue =
          params.row?.originalRow?.source || params.row?.source;
        const isCodebeamer =
          typeof sourceValue === "string" &&
          sourceValue.trim().toLowerCase() == "codebeamer";

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>{params.value}</span>
            <IconButton
              size="small"
              onClick={() => handleEditRow(params.row)}
              disabled={isCodebeamer}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },

    {
      field: "req_number",
      headerName: "Requirement Number",
      flex: 1,
    },

    {
      field: "type_of_changes",
      headerName: "Type of Changes",
      flex: 1,
    },

    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      renderCell: (params) => {
        const descriptionText =
          typeof params.value === "string" && params.value.trim()
            ? params.value
            : "-";

        return (
          <Tooltip title={descriptionText} arrow placement="top">
            <Typography
              sx={{
                fontSize: "12px",
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {descriptionText}
            </Typography>
          </Tooltip>
        );
      },
    },

    {
      field: "effective_from",
      headerName: "Effective From",
      flex: 1,
    },

    {
      field: "valid_till",
      headerName: "Valid Till",
      flex: 1,
    },
    {
      field: "source",
      headerName: "Source",
      flex: 1,
    },
  ];

  const gridRows = Array.isArray(tableRowData)
    ? tableRowData
      .filter((row) => row && row.lr_id) // 🔒 must have unique id
      .map((row, idx) => ({
        id: String(row.lr_id), // ✅ always string
        s_no: (page - 1) * rowsPerPage + idx + 1,

        req_number:
          typeof row.req_number === "string" && row.req_number.trim()
            ? row.req_number
            : "-",

        type_of_changes:
          typeof row.type_of_changes === "string" &&
            row.type_of_changes.trim()
            ? row.type_of_changes
            : "-",

        description:
          typeof row.description === "string" && row.description.trim()
            ? row.description
            : "-",

        effective_from:
          typeof row.effective_from === "string" && row.effective_from.trim()
            ? row.effective_from
            : "-",

        valid_till:
          typeof row.valid_till === "string" && row.valid_till.trim()
            ? row.valid_till
            : "-",

        source:
          typeof row.source === "string" && row.source.trim()
            ? row.source
            : "-",

        // 🔥 keep original row for actions/editing
        originalRow: row,
      }))
    : [];


  const resetAllFields = () => {
    setReqNumber("");
    setReqNumberError("");

    setChapter([]);
    setChapterError("");

    setDescription("");
    setDescriptionError("");

    setEffectiveFrom(null);
    setValidTill(null);

    setChaptersOptionsList([]);

    setOpenForVersion(false);
    setOpenEditLR(false);
    setIsOverrideChecked(false);

    setStatus("");
  };

  const handleOpenCloseAddLS = (newValue) => {
    setAddLRDialog(newValue);
    if (newValue === true) {
      resetAllFields();
    }
  };
  const handleOpenCloseAddLR = (newValue) => {
    setOpenEditLR(newValue);
  };

  const singleColumnRows = [1, 2, 3, 4];
  const componentMap = {
    ChipsTextField,
    CustomFormTextField,
    LegalCustomDatePicker,
    EnterSelectTextField,
    CustomStatusOverrideSelect,
  };
  const formLayoutAdd = [
    {
      component: "CustomFormTextField",
      props: {
        label: "Legal Requirement No",
        required: true,
        type: "text",
        fldType: "text",
        value: reqNumber,
        setValue: setReqNumber,
        placeholder: "Enter Legal Requirement No",
      },
    },
    {
      component: "LegalCustomDatePicker",
      props: {
        label: "Effective From - Valid Till",

        effectiveFrom: effectiveFrom,
        validTill: validTill,
        setEffectiveFrom: setEffectiveFrom,
        setValidTill: setValidTill,

        required: true,
      },
    },

    {
      component: "EnterSelectTextField",
      props: {
        required: true,
        size: "small",
        label: "Chapter",
        placeholder: "Select or Enter Chapter",
        optionsList: chaptersOptionsList,
        value: chapter,
        setValue: setChapter,
      },
    },

    {
      component: "CustomFormTextField",
      props: {
        label: "Description",
        required: true,
        value: description,
        setValue: setDescription,
        placeholder: "Enter Description",
      },
    },
    {
      component: "CustomStatusOverrideSelect",
      props: {
        label: "Select Status",
        statusOptions: [
          "NEW",
          "MODIFIED",
          "SPLIT",
          "MERGED",
          "REMOVED",
          "UNCHANGED",
        ],
        value: status,
        setValue: setStatus,
        isOverrideChecked: isOverrideChecked,
        setIsOverrideChecked: setIsOverrideChecked,
        required: true,
      },
    },
  ];
  const isAddDisabledVC =
    !reqNumber?.trim() ||
    !chapter?.length > 0 ||
    !description?.trim() ||
    !status ||
    !effectiveFrom ||
    !validTill;
  const formLayoutEdit = [
    {
      component: "CustomFormTextField",
      props: {
        label: "Legal Requirement No",
        required: true,
        disabled: true,
        type: "text",
        fldType: "text",
        value: reqNumber,
        setValue: setReqNumber,
        placeholder: "Enter Legal Requirement No",
      },
    },
    {
      component: "LegalCustomDatePicker",
      props: {
        label: "Effective From - Valid Till",

        effectiveFrom: effectiveFrom,
        validTill: validTill,
        setEffectiveFrom: setEffectiveFrom,
        setValidTill: setValidTill,

        required: true,
      },
    },

    {
      component: "EnterSelectTextField",
      props: {
        size: "small",
        disabled: true,
        label: "Select or Enter Chapter",
        placeholder: "Select or Enter Chapter",
        optionsList: chaptersOptionsList,
        value: chapter,
        setValue: setChapter,
        required: true,
      },
    },

    {
      component: "CustomFormTextField",
      props: {
        label: "Description",
        required: true,
        value: description,
        setValue: setDescription,
        placeholder: "Enter Description",
      },
    },
    {
      component: "CustomStatusOverrideSelect",
      props: {
        label: "Status",
        required: true,
        statusOptions: [
          "NEW",
          "MODIFIED",
          "SPLIT",
          "MERGED",
          "REMOVED",
          "UNCHANGED",
        ],
        value: status,
        setValue: setStatus,
        isOverrideChecked: isOverrideChecked,
        setIsOverrideChecked: setIsOverrideChecked,
      },
    },
  ];

  const generateTableRows = (rows = []) => {
    if (!Array.isArray(rows)) return [];

    return rows.map((row, index) => {
      const isEditing = isEditingRowId === row.id;
      const isNewRow = row?.isNew === true;

      return {
        id: row?.lr_id || row?.id || `temp-${index}`,
        s_no: index + 1,

        requirement_number:
          isEditing || isNewRow ? (
            <input
              style={{ width: "100%", padding: 6 }}
              value={editRowData.requirement_number || ""}
              onChange={(e) =>
                handleEditChange("requirement_number", e.target.value)
              }
            />
          ) : (
            row?.req_number || "N/A"
          ),

        status:
          isEditing || isNewRow ? (
            <input
              style={{ width: "100%", padding: 6 }}
              value={editRowData.status || ""}
              onChange={(e) => handleEditChange("status", e.target.value)}
            />
          ) : (
            row?.type_of_changes || "UNCHANGED"
          ),

        description:
          isEditing || isNewRow ? (
            <input
              style={{ width: "100%", padding: 6 }}
              value={editRowData.description || ""}
              onChange={(e) => handleEditChange("description", e.target.value)}
            />
          ) : (
            row?.description || "-"
          ),

        /* ✅ SINGLE DATE RANGE COLUMN */
        effective_valid_range:
          isEditing || isNewRow ? (
            <LegalDateRangeInput
              value={{
                effective_from: editRowData.effective_from,
                valid_till: editRowData.valid_till,
              }}
              onChange={(val) => {
                handleEditChange("effective_from", val.effective_from);
                handleEditChange("valid_till", val.valid_till);
              }}
            />
          ) : (
            `${row?.effective_from || "-"} → ${row?.valid_till || "-"}`
          ),

        actions:
          isEditing || isNewRow ? (
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={handleSaveRow}
                style={{
                  background: "green",
                  color: "white",
                  border: "none",
                  padding: "4px 10px",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                ✓ Save
              </button>

              <button
                onClick={handleCancelRow}
                style={{
                  background: "red",
                  color: "white",
                  border: "none",
                  padding: "4px 10px",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                ✖ Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setIsEditingRowId(row.id);
                setEditRowData(row);
              }}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                background: "#f1f1f1",
                border: "1px solid #aaa",
                cursor: "pointer",
              }}
            >
              Edit
            </button>
          ),
      };
    });
  };
  const handleCancelRow = () => {
    if (editRowData?.isNew) {
      setTableRowData((prev) =>
        prev.filter((row) => row.id !== editRowData.id),
      );
    }

    // Reset editing state
    setIsEditingRowId(null);
    setEditRowData({});
  };

  const handleAddLegalRequirement = async ({
    reqNumber,
    chapter,
    description,
    effectiveFrom,
    validTill,
    status_manual,
  }) => {
    const currentTime = new Date().toISOString();

    const payload = {
      ...legalRequirements.add_legal_requirements,
      params: {
        ls_id: Number(standard?.ls_id),
        req_number: reqNumber,
        chapter,
        description,
        status_manual: status,
        ...(validTill
          ? { valid_till: dayjs(validTill).format("MM-YYYY") }
          : {}),
        effective_from: dayjs(effectiveFrom).format("MM-YYYY"),
        current_time: currentTime,
      },
      show_toast: true,
    };

    const resp = await dispatch(HandleApiActions(payload));

    if (!resp?.error) {
      // refresh lists after success
      refreshLegalStandards(false);
      handleGetLegalRequirementsList(standard.ls_id, selectedChapter);
      handleOpenCloseAddLS(false);
    }
  };

  const handleUpdateLegalRequirement = ({
    lr_id,
    description,
    effectiveFrom,
    validTill,
    status,
  }) => {
    if (lr_id) {
      let currentTime = new Date().toISOString();

      dispatch(
        HandleApiActions({
          ...legalRequirements.update_legal_requirements,
          params: {
            lr_id,
            ...(description ? { description } : null),
            ...(validTill != null
              ? { valid_till: dayjs(validTill).format("MM-YYYY") }
              : {}),
            effective_from: dayjs(effectiveFrom).format("MM-YYYY"),
            current_time: currentTime,
            status_manual: status,
          },
          show_toast: true,
        }),
      ).then(() => {
        // get the legal standards list
        refreshLegalStandards(false);
        handleGetLegalRequirementsList(standard.ls_id, selectedChapter);
      });

      setOpenEditLR(false);
    }
  };



  const isFormValid = (d) =>
    d.name?.trim() &&
    d.description?.trim() &&
    d.revision?.trim() &&
    d.effective_from &&
    d.valid_till &&
    Array.isArray(d.markets) &&
    d.markets.length > 0;
  const hasChanges = () => {
    return JSON.stringify(editRowData) !== JSON.stringify(initialStandardData);
  };

  return (
    <div>
      {/* Version + Chapters Row */}
      <Box sx={{ display: "flex", gap: 3, width: "100%" }}>
        {/* Version */}
        <Box sx={{ width: "25%" }}>

          <CustomSelectV2
            showlabelNew={false}
            label="Version"
            required={false}
            enableSearch
            selectedValue={selectedVersion}
            handleOnChange={(e) => setSelectedVersion(e.target.value)}
            optionsList={versionOptions}
            sltFldStyle={{
              width: "100%", // ✅ full width inside 50% container
              minHeight: "45px",
              fontSize: "0.95rem",
              borderRadius: 0,
            }}
          />
        </Box>

        {/* Chapters */}
        <Box sx={{ width: "25%" }}>

          <CustomSelectV2
            label="Tags / Chapters"
            showlabelNew={false}
            enableSearch
            required={false}
            selectedValue={selectedChapter}
            handleOnChange={(e) => setSelectedChapter(e.target.value)}
            optionsList={(standard?.chapters || []).map((item) => ({
              label: item,
              value: item,
            }))}
            sltFldStyle={{
              width: "100%", // ✅ aligned same as version dropdown
              minHeight: "45px",
              fontSize: "0.95rem",
              borderRadius: 0,
            }}
          />
        </Box>
      </Box>

      {/* RIGHT SIDE — Add Legal Requirement Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          width: "100%",
          mt: 2,
          mb: 1,
          pr: 1,
        }}
      >
        <Box sx={{ mr: 1 }}></Box>
        <Box sx={{ mr: 1 }}>
          <CustomButtonV2
            type="submit"
            size="small"
            variant="contained"
            btnName="Sync"
            handleOnClick={() => setOpenSyncDialog(true)}
            startIcon={<SyncIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
          />
        </Box>
        <Box>
          <AddButtonV2 handleOnClick={() => handleOpenCloseAddLS(true)} />
        </Box>
      </Box>

      <Box
        sx={{
          height: 500,
          overflow: "auto",
          "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "#fff",
          },
        }}
      >

        <CustomDataGridV2
          totalItems={lrCount}
          rows={gridRows}
          page={page}
          rowsPerPage={rowsPerPage}
          columns={COLUMNS}
          isMultiLine={true}
          showPagination={lrCount > 0 ? true : false}
          getRowHeight={(params) => (params.model?.isDetailRow ? "auto" : 52)}
          handlePageChange={(newPage) => setPage(newPage)}
        />
      </Box>

      {addLRDialog && (
        <AddDialog
          disabled={isAddDisabledVC}
          componentMap={componentMap}
          formLayout={formLayoutAdd}
          singleColumnRows={singleColumnRows}
          open={addLRDialog}
          onClose={() => handleOpenCloseAddLS(false)}
          title={"Add Legal Requirement"}
          handleClickAddDialog={() => {
            handleAddLegalRequirement({
              reqNumber,
              chapter,
              description,
              effectiveFrom,
              validTill,
              status,
            });
          }}
        />
      )}

      {openEditLR && (
        <AddDialog
          disabled={isEditDisabledLR()}
          componentMap={componentMap}
          formLayout={formLayoutEdit}
          singleColumnRows={singleColumnRows}
          open={openEditLR}
          onClose={() => handleOpenCloseAddLR(false)}
          title={"Update Legal Requirement"}
          handleClickAddDialog={() => {
            handleUpdateLegalRequirement({
              lr_id: isEditingRowId,
              description,
              effectiveFrom,
              validTill,
              status,
            });
          }}
        />
      )}
      <SyncLegalRequirementsDialog
        open={openSyncDialog}
        onClose={() => setOpenSyncDialog(false)}
        selectedStandard={standard}
        selectedChapter={selectedChapter}
        onSyncSuccess={() => {
          refreshLegalStandards(false);
          handleGetLegalRequirementsList(standard.ls_id, selectedChapter);
        }}
      />
    </div>
  );
}

export default CustomLegalRequirements;

const LegalDateRangeInput = ({ value = {}, onChange }) => {
  const { effective_from, valid_till } = value;

  const handleChange = (field, val) => {
    onChange({
      effective_from: field === "effective_from" ? val : effective_from,
      valid_till: field === "valid_till" ? val : valid_till,
    });
  };

  return (
    <div style={{ display: "flex", gap: 6 }}>
      <input
        type="date"
        style={{ width: "100%", padding: 6 }}
        value={effective_from || ""}
        onChange={(e) => handleChange("effective_from", e.target.value)}
      />

      <input
        type="date"
        style={{ width: "100%", padding: 6 }}
        value={valid_till || ""}
        onChange={(e) => handleChange("valid_till", e.target.value)}
      />
    </div>
  );
};
